import { useState, useMemo, useEffect } from "react";
import { useAuthStore } from "@/store/authStore";
import { useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import * as services from "../services";
import api from "@/lib/API";

export const useInventoryLogic = () => {
  const { user } = useAuthStore();
  const projects = [];
  const notifications = [];
  const addNotification = () => {};

  const [inventory, setInventoryState] = useState([]);
  const [masterInventory, setMasterInventory] = useState([]);
  const [materialRequests, setMRState] = useState([]);
  const [materialReturns, setReturnsState] = useState([]);
  const [sales, setSalesState] = useState([]);
  const [purchaseReturns, setPurchaseReturnsState] = useState([]);
  const [saleReturns, setSaleReturnsState] = useState([]);
  const [purchasesState, setPurchasesState] = useState([]);
  const [projectsLocal, setProjectsLocal] = useState([]);
  const [loading, setLoading] = useState(true);

  const [cats, setCats] = useState([]);
  const [units, setUnits] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [stores, setStores] = useState([]);
  const [masterItems, setMasterItems] = useState([]);

  const location = useLocation();

  const pathParts = location.pathname.split("/").filter(Boolean);
  const initialProject = "verve";
  const initialSubTab = pathParts.includes("notifications") ? "notifications" : "items";

  const [activeTab, setActiveTab] = useState(initialProject);
  const [subActiveTab, setSubActiveTab] = useState(initialSubTab);
  const [viewMode, setViewMode] = useState("inventory");
  const [inventorySubTab, setInventorySubTab] = useState("items"); // options: items, purchases, sales, open_stock
  const [returnsSubTab, setReturnsSubTab] = useState("master"); // options: master, purchases, sales
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    category: "all",
    supplier: "all",
    stockStatus: "all",
    date: "all",
    fromDate: "",
    toDate: ""
  });
  const [sortBy, setSortBy] = useState("newest");
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [restockItem, setRestockItem] = useState(null);
  const [restockQty, setRestockQty] = useState("");
  const [restockReturn, setRestockReturn] = useState(null); // Sale Return record being restocked
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState({
    name: "", sku: "", itemCode: "", category: "", unit: "", unitPrice: 0, minQty: 0, availableQty: 0, supplier: "", serviceType: activeTab,
    // Item Configuration
    type: "Stockable", brand: "", hsnCode: "", isUnique: false, uniqueName: "", hasBatchExpiry: false, expiryDate: "", barcode: "", description: "",
    // Billing & Financials
    invoiceNo: "", invoiceDate: new Date().toISOString().split('T')[0], businessUnit: "VERVE KITCHENS", store: "",
    rate: 0, quantity: 1, baseAmount: 0, discP: 0, discount: 0, taxable: 0, taxP: 0, bvPoints: 0,
    igst: 0, cgst: 0, sgst: 0, taxAmount: 0,
    netBill: 0, chargeAmount: 0, adjustments: 0, netPayment: 0,
    items: []
  });
  const [selReq, setSelReq] = useState(null);
  const [selReturn, setSelReturn] = useState(null);
  const [saleOpen, setSaleOpen] = useState(false);
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [purchaseReturnOpen, setPurchaseReturnOpen] = useState(false);
  const [saleReturnOpen, setSaleReturnOpen] = useState(false);
  const [openStockOpen, setOpenStockOpen] = useState(false);
  const [selSale, setSelSale] = useState(null);
  const [selPR, setSelPR] = useState(null);
  const [selSR, setSelSR] = useState(null);
  const [initialReturnInvoice, setInitialReturnInvoice] = useState("");
  const [selectedPRForPayment, setSelectedPRForPayment] = useState(null);
  const [dispatchReq, setDispatchReq] = useState(null);
  const [viewItem, setViewItem] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  // Real-time calculations for the form
  useEffect(() => {
    const rate = Number(form.rate) || 0;
    const qty = Number(form.quantity) || 1;
    const discP = Number(form.discP) || 0;
    const igst = Number(form.igst) || 0;
    const sgst = Number(form.sgst) || 0;
    const cgst = Number(form.cgst) || 0;
    const chargeAmount = Number(form.chargeAmount) || 0;
    const adjustments = Number(form.adjustments) || 0;
    const taxP = Number(form.taxP) || 0;

    const baseAmount = rate * qty;
    const discountAmount = (baseAmount * discP) / 100;
    const taxable = baseAmount - discountAmount;

    // Calculate total tax based on tax percentage or manual GST entries
    let taxTotal = igst + cgst + sgst;
    if (taxP > 0) {
      taxTotal = (taxable * taxP) / 100;
    }

    const netBill = taxable + taxTotal;

    // Calculate total net payment for the WHOLE invoice/form
    const itemsTotal = (form.items || []).reduce((sum, it) => sum + (Number(it.rowTotal || it.netPayment) || 0), 0);
    const netPayment = netBill + (Number(form.chargeAmount) || 0) + (Number(form.adjustments) || 0) + itemsTotal;

    setForm(prev => {
      // If we are resetting (name is empty), we only care about updating from itemsTotal
      if (!prev.name && (prev.items?.length || 0) > 0) {
        const currentTotal = itemsTotal + Number(prev.adjustments || 0);
        if (prev.netPayment === currentTotal) return prev;
        return { ...prev, netPayment: currentTotal };
      }

      if (
        prev.baseAmount === baseAmount &&
        prev.discount === discountAmount &&
        prev.taxable === taxable &&
        prev.taxAmount === taxTotal &&
        prev.netBill === netBill &&
        prev.netPayment === netPayment &&
        Math.abs(Number(prev.unitPrice || 0) - (qty > 0 ? (netBill / qty) : Number(prev.unitPrice || 0))) < 0.01
      ) return prev;

      return {
        ...prev,
        baseAmount,
        discount: discountAmount,
        taxable,
        taxAmount: taxTotal,
        netBill,
        netPayment,
        unitPrice: qty > 0 ? (netBill / qty) : prev.unitPrice,
        availableQty: qty
      };

    });
  }, [form.rate, form.quantity, form.discP, form.igst, form.cgst, form.sgst, form.chargeAmount, form.adjustments, form.taxP, form.items?.length, form.unitPrice]);

  const toArray = (val) => Array.isArray(val) ? val : [];

  const fetchMaterialRequests = async () => {
    try {
      const res = await services.fetchMaterialRequests({ limit: 50000 });
      setMRState(toArray(res.data?.requests || res.data));
    } catch (err) {
      console.error("Failed to fetch material requests:", err);
    }
  };

  const fetchMaterialReturns = async () => {
    try {
      const res = await services.fetchMaterialReturns();
      setReturnsState(toArray(res.data?.returns || res.data));
    } catch (err) {
      console.error("Failed to fetch material returns:", err);
    }
  };



  const fetchPurchaseReturns = async () => {
    try {
      const res = await services.fetchPurchaseReturns();
      setPurchaseReturnsState(toArray(res.data?.purchaseReturns || res.data?.data || res.data));
    } catch (err) {
      console.error("Failed to fetch purchase returns:", err);
    }
  };

  const fetchSaleReturns = async () => {
    try {
      const res = await services.fetchSaleReturns();
      setSaleReturnsState(toArray(res.data?.data || res.data?.sales || res.data));
    } catch (err) {
      console.error("Failed to fetch sale returns:", err);
    }
  };

  const refreshAll = async (showLoading = false) => {
    try {
      if (showLoading) setLoading(true);
      const role = String(user?.role || "").toLowerCase().trim();
      const canReadExecution = [
        "owner", "admin", "verve-admin", "sales-manager", 'designer',
        'store_incharge', "finance-manager", 'installation_team', "telecaller",
        "accountant"
      ].includes(role);

      const [invData, mrData, retData, prData, srData, projData,
        catRes, unitRes, supplierRes, brandRes, storeRes, itemRes, masterRes, purRes, salesRes] = await Promise.all([
        services.fetchInventory({ serviceType: activeTab, limit: 5000 }).catch(() => ({ data: [] })),
        services.fetchMaterialRequests({ limit: 5000 }).catch(() => ({ data: { requests: [] } })),
        services.fetchMaterialReturns().catch(() => ({ data: { returns: [] } })),
        services.fetchPurchaseReturns({ limit: 5000 }).catch(() => ({ data: { purchaseReturns: [] } })),
        services.fetchSaleReturns({ limit: 5000 }).catch(() => ({ data: { saleReturns: [] } })),
        (canReadExecution ? api.get('/verve-projects') : Promise.resolve({ projects: [] })).catch(() => ({ projects: [] })),
        api.get('/inventory/categories?limit=10000&status=ACTIVE').catch(() => ({ data: [] })),
        api.get('/inventory/units?limit=10000&status=ACTIVE').catch(() => ({ data: [] })),
        api.get('/inventory/vendors?limit=10000&status=ACTIVE').catch(() => ({ data: [] })),
        api.get('/inventory/brands?limit=10000&status=ACTIVE').catch(() => ({ data: [] })),
        api.get('/inventory/stores?limit=10000&status=ACTIVE').catch(() => ({ data: [] })),
        api.get('/inventory/settings?settingType=item&limit=10000&status=ACTIVE').catch(() => ({ data: [] })),
        services.fetchInventoryMaster({ serviceType: activeTab, limit: 5000 }).catch(() => ({ data: [] })),
        services.fetchPurchases({ limit: 5000 }).catch(() => ({ data: [] })),
        api.get('/inventory/sales').catch(() => ({ data: { combined: [] } }))
      ]);

      // Safely extract arrays from response objects based on known backend formats
      const toArray = (val) => Array.isArray(val) ? val : [];
      setInventoryState(toArray(invData.data?.data || invData.data));
      const masterData = toArray(masterRes.data?.data || masterRes.data);
      if (masterData.length > 0) {
        setMasterInventory(masterData);
      } else {
        const rawData = toArray(invData.data?.data || invData.data);
        setMasterInventory(rawData.map(item => ({
          ...item,
          _stock: item.stock || 0,
          availableQty: item.availableQty ?? item.stock ?? 0,
          minQty: item.minStock || item.minQty || 0,
        })));
      }
      setMRState(toArray(mrData.data?.requests || mrData.data));
      setReturnsState(toArray(retData.data?.returns || retData.data));
      setPurchaseReturnsState(toArray(prData?.data?.purchaseReturns || prData?.data?.data || prData?.data));
      setSaleReturnsState(toArray(srData.data?.data || srData.data?.sales || srData.data));
      setSalesState(toArray(salesRes.data?.combined || salesRes.data?.sales || salesRes.data));
      setPurchasesState(toArray(purRes.data?.data || purRes.data?.purchases || purRes.data));

      const pList = projData.projects || projData.data?.projects || (Array.isArray(projData.data) ? projData.data : []);
      setProjectsLocal(pList);

      const ext = (res) => (res && res.data && res.data.data) ? res.data.data : ((res && res.data) ? res.data : (res || []));
      setCats(ext(catRes));
      setUnits(ext(unitRes));
      setSuppliers(ext(supplierRes));
      setBrands(ext(brandRes));
      setStores(ext(storeRes));
      setMasterItems(ext(itemRes));
    } catch (err) {
      console.error("Failed to load inventory data:", err);
    } finally {
      if (showLoading) setLoading(false);
    }
  };


  useEffect(() => {
    refreshAll(true);
  }, [activeTab, user?.role]);

  useEffect(() => {
    const parts = location.pathname.split("/").filter(Boolean);
    
    if (parts.includes("verve")) setActiveTab("verve");

    if (parts.includes("notifications")) setSubActiveTab("notifications");
    else setSubActiveTab("items");
  }, [location.pathname]);

  const activeInventoryRaw = useMemo(() => inventory.filter(i => i.serviceType === activeTab), [inventory, activeTab]);

  const activeInventory = useMemo(() => {
    // Merge InventoryItems with MasterItems to handle hybrid inventory
    const items = [...activeInventoryRaw];

    // Track names in batch inventory to avoid double counting master items
    const batchNames = new Set(activeInventoryRaw.map(i => (i.name || i.itemName || '').trim()));

    // Add MasterItems that are not already present in the batch-wise inventory
    masterItems.forEach(mi => {
      const name = (mi.itemName || mi.name || '').trim();
      if (mi.active !== false && mi.serviceType === activeTab && !batchNames.has(name)) {
        const getLabel = (field) => {
          if (!field) return "";
          if (typeof field === 'object') return field.categoryName || field.brandName || field.unitName || field.title || field.name || "";
          return field;
        };

        items.push({
          _id: mi._id,
          name: mi.itemName,
          itemName: mi.itemName,
          category: getLabel(mi.category),
          unit: getLabel(mi.units),
          brand: getLabel(mi.brand),
          availableQty: mi.stockQty || 0,
          minQty: mi.minQty || 0,
          unitPrice: mi.purchaseAmount || 0,
          itemCode: mi.itemCode,
          serviceType: mi.serviceType,
          itemType: mi.itemType,
          isMaster: true,
          item: mi
        });
      }
    });

    return items;
  }, [activeInventoryRaw, masterItems, activeTab]);

  const activeNotifications = useMemo(() => notifications.filter(n => n.serviceType === activeTab), [notifications, activeTab]);

  // Aggregate ALL inventory for the project to calculate stats correctly
  // Instead of recalculating from activeInventory (which only has purchases), 
  // we just use the filtered master inventory that correctly tracks total stock.
  // Wait, filtered is defined below. So we can just define filtered first.
  const noStock = masterInventory.filter(i => i.availableQty <= 0);

  const filtered = useMemo(() => {
    const result = masterInventory.filter(i => {
      // ── SEARCH ──────────────────────────────────────────────
      const itemName = i.name || i.item?.itemName || "";
      const itemCode = i.itemCode || i.item?.itemCode || "";
      const searchMatch = !search || (
        itemName.toLowerCase().includes(search.toLowerCase()) ||
        itemCode.toLowerCase().includes(search.toLowerCase())
      );

      // ── CATEGORY ────────────────────────────────────────────
      // category on the record is already the populated Category object (or an ObjectId string)
      const cat = i.category;
      const catId = cat?._id ? cat._id.toString() : (cat ? cat.toString() : "");
      const catMatch = filters.category === "all" || catId === filters.category;

      // ── VENDOR / SUPPLIER ───────────────────────────────────
      // vendor is populated from the most recent purchase for this item
      const vend = i.vendor;
      const vendorId = vend?._id ? vend._id.toString() : (vend ? vend.toString() : "");
      const supplierMatch = filters.supplier === "all" || vendorId === filters.supplier;

      // ── STOCK STATUS ─────────────────────────────────────────
      let statusMatch = true;
      const avail = Number(i.availableQty) || 0;
      const minQ  = Number(i.minQty) || 0;
      if (filters.status === "low") statusMatch = avail <= minQ && avail > 0 && minQ > 0;
      else if (filters.status === "out") statusMatch = avail <= 0;
      else if (filters.status === "in")  statusMatch = avail > minQ;

      // ── DATE RANGE ───────────────────────────────────────────
      let dateMatch = true;

      // Date picker (fromDate / toDate) takes priority over preset buttons
      if (filters.fromDate || filters.toDate) {
        const recordDate = i.createdAt ? new Date(i.createdAt) : null;
        if (!recordDate || isNaN(recordDate)) {
          dateMatch = false;
        } else {
          if (filters.fromDate) {
            const start = new Date(filters.fromDate);
            start.setHours(0, 0, 0, 0);
            if (recordDate < start) dateMatch = false;
          }
          if (filters.toDate) {
            const end = new Date(filters.toDate);
            end.setHours(23, 59, 59, 999);
            if (recordDate > end) dateMatch = false;
          }
        }
      } else if (filters.date && filters.date !== "all") {
        // Quick preset buttons (T / W / M / Y)
        const recordDate = i.createdAt ? new Date(i.createdAt) : null;
        if (!recordDate || isNaN(recordDate)) {
          dateMatch = false;
        } else {
          const now = new Date();
          const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
          const todayEnd   = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

          if (filters.date === "today") {
            dateMatch = recordDate >= todayStart && recordDate <= todayEnd;
          } else if (filters.date === "this_week") {
            const weekStart = new Date(todayStart);
            weekStart.setDate(weekStart.getDate() - 6);
            dateMatch = recordDate >= weekStart && recordDate <= todayEnd;
          } else if (filters.date === "this_month") {
            const monthStart = new Date(todayStart);
            monthStart.setDate(monthStart.getDate() - 29);
            dateMatch = recordDate >= monthStart && recordDate <= todayEnd;
          } else if (filters.date === "this_year") {
            const yearStart = new Date(todayStart);
            yearStart.setDate(yearStart.getDate() - 364);
            dateMatch = recordDate >= yearStart && recordDate <= todayEnd;
          }
        }
      }

      return searchMatch && catMatch && supplierMatch && statusMatch && dateMatch;
    });

    // Sort the filtered result
    result.sort((a, b) => {
      switch (sortBy) {
        case "newest": return new Date(b.createdAt) - new Date(a.createdAt);
        case "oldest": return new Date(a.createdAt) - new Date(b.createdAt);
        case "name_asc": return (a.name || "").localeCompare(b.name || "");
        case "name_desc": return (b.name || "").localeCompare(a.name || "");
        case "stock_low": return (a.availableQty || 0) - (b.availableQty || 0);
        case "stock_high": return (b.availableQty || 0) - (a.availableQty || 0);
        default: return 0;
      }
    });

    return result;
  }, [masterInventory, search, filters, sortBy]);

  const allAggregatedItems = filtered;
  const filterCats = useMemo(() => ["all", ...Array.from(new Set(activeInventory.map(i => i.category)))], [activeInventory]);
  const lowStock = masterInventory.filter(i => i.availableQty <= i.minQty && i.availableQty > 0);

  const requestStatusOptions = useMemo(() => {
    const rawStatuses = [...new Set(materialRequests.map(r => String(r.status || "").trim().toLowerCase()))];
    const options = [];
    
    // Helper to check if any raw status falls into a category
    const has = (arr) => rawStatuses.some(s => arr.includes(s));
    
    if (has(['pending'])) options.push({ value: 'pending', label: 'Pending' });
    if (has(['pending_store', 'pending store'])) options.push({ value: 'pending_store', label: 'Pending Store' });
    if (has(['pending_owner_approval', 'pending owner approval', 'awaiting_owner_approval', 'awaiting owner approval'])) options.push({ value: 'pending_owner_approval', label: 'Awaiting Owner Approval' });
    if (has(['approved', 'owner_approved', 'owner approved'])) options.push({ value: 'approved', label: 'Approved' });
    if (has(['dispatched'])) options.push({ value: 'dispatched', label: 'Dispatched' });
    if (has(['in_transit', 'in transit'])) options.push({ value: 'in_transit', label: 'In Transit' });
    if (has(['delivered'])) options.push({ value: 'delivered', label: 'Delivered' });
    if (has(['partial', 'partially_delivered', 'partially delivered'])) options.push({ value: 'partially_delivered', label: 'Partially Delivered' });
    if (has(['rejected'])) options.push({ value: 'rejected', label: 'Rejected' });
    if (has(['fulfilled'])) options.push({ value: 'fulfilled', label: 'Fulfilled' });
    
    return options;
  }, [materialRequests]);

  const filteredRequests = useMemo(() => {
    const term = search?.toLowerCase() || "";
    return materialRequests.filter(r => {
      // Do not exclude requests with 0 items, so store manager can still see and reject/process them
      // if (!r.items || r.items.length === 0) return false;

      // Hide fulfilled requests by default, unless explicitly requested
      if ((!filters?.status || filters.status === 'all') && r.status === "fulfilled") return false;
      if (filters?.status && filters.status !== 'all') {
         const rowStatus = String(r.status || "").trim().toLowerCase();
         let filterStatus = String(filters.status).trim().toLowerCase();
         
         if (rowStatus !== filterStatus) {
            // Check for special mappings and allow them
            const isPartialMatch = (filterStatus === 'partially_delivered' || filterStatus === 'partial') && 
                                   (rowStatus === 'partial' || rowStatus === 'partially_delivered' || rowStatus === 'partially delivered');
            const isPendingOwner = filterStatus === 'pending_owner_approval' && (rowStatus === 'pending owner approval' || rowStatus === 'pending_owner_approval' || rowStatus === 'awaiting_owner_approval' || rowStatus === 'awaiting owner approval');
            const isPendingStore = filterStatus === 'pending_store' && (rowStatus === 'pending store' || rowStatus === 'pending_store');
            
            // Map 'approved' in dropdown to both 'approved' and 'owner_approved'
            const isApproved = filterStatus === 'approved' && (rowStatus === 'approved' || rowStatus === 'owner approved' || rowStatus === 'owner_approved');

            if (!isPartialMatch && !isPendingOwner && !isPendingStore && !isApproved) {
               return false;
            }
         }
      }

      if (!term) return true;

      const populated = typeof r.project === 'object' && r.project ? r.project : null;
      const byObjectId = populated?._id?.toString() || r.projectId?.toString() || (!populated ? r.project?.toString() : null);
      const byStringId = populated?.projectId || null;
      const matchedProj = projectsLocal.find(p =>
        (byObjectId && (p._id?.toString() === byObjectId || p.id?.toString() === byObjectId)) ||
        (byStringId && p.projectId === byStringId)
      );
      
      const proj = matchedProj || populated || r.projectDetails;
      const customerName = (proj?.customerName || proj?.customer?.name || r.projectDetails?.customerName || "").toLowerCase();

      return customerName.includes(term);
    });
  }, [materialRequests, projectsLocal, search, filters]);

  const filteredReturns = useMemo(() => {
    const term = search?.toLowerCase() || "";
    return materialReturns.filter(r => {
      if (!term) return true;
      const proj = r.projectDetails || projectsLocal.find(p =>
        (p._id?.toString() === r.projectId?.toString()) ||
        (p.id?.toString() === r.projectId?.toString()) ||
        (p.projectId === r.projectId)
      );

      const customerId = (proj?.leadId?.customId || proj?.leadRef?.customId || proj?.customerCustomId || "").toLowerCase();
      const customerName = (proj?.customerName || r.projectDetails?.customerName || "").toLowerCase();
      const driverName = (r.driverName || "").toLowerCase();
      const vehicleNo = (r.vehicleNo || "").toLowerCase();

      return customerName.includes(term) ||
        customerId.includes(term) ||
        driverName.includes(term) ||
        vehicleNo.includes(term) ||
        r.projectId?.toLowerCase().includes(term);
    });
  }, [materialReturns, projectsLocal, search]);

  const filteredSales = useMemo(() => {
    const term = search?.toLowerCase() || "";
    return sales.filter(s => {
      const customerName = typeof s.customer === 'object' ? (s.customer?.name || s.customer?.title || "") : (s.customer || "");
      const searchMatch = !term || (
        (s.invoiceNo || "").toLowerCase().includes(term) ||
        customerName.toLowerCase().includes(term) ||
        (s.customerPhone || "").toLowerCase().includes(term)
      );

      const customerMatch = !filters.customer || filters.customer === 'all' || 
        customerName.toLowerCase() === filters.customer.toLowerCase();

      let dateMatch = true;
      const d = new Date(s.date || s.createdAt);
      if (filters.date && filters.date !== 'all') {
        const today = new Date();
        if (filters.date === 'today') {
          dateMatch = d.toDateString() === today.toDateString();
        } else if (filters.date === 'this_week') {
          const currentDay = today.getDay();
          const diff = today.getDate() - currentDay + (currentDay === 0 ? -6 : 1);
          const start = new Date(today.setDate(diff));
          start.setHours(0,0,0,0);
          dateMatch = d >= start;
        } else if (filters.date === 'this_month') {
          dateMatch = d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear();
        } else if (filters.date === 'this_year') {
          dateMatch = d.getFullYear() === today.getFullYear();
        }
      } else if (filters.fromDate || filters.toDate) {
        if (filters.fromDate) {
          const start = new Date(filters.fromDate);
          start.setHours(0,0,0,0);
          if (d < start) dateMatch = false;
        }
        if (filters.toDate) {
          const end = new Date(filters.toDate);
          end.setHours(23,59,59,999);
          if (d > end) dateMatch = false;
        }
      }

      let categoryMatch = true;
      if (filters.category && filters.category !== 'all') {
        categoryMatch = (s.items || []).some(it => {
          const cat = typeof it.item?.category === 'object' ? (it.item.category?._id || it.item.category?.categoryName || it.item.category?.title) : (it.item?.category || it.category);
          return String(cat) === filters.category || String(it.item?.category) === filters.category;
        });
      }

      return searchMatch && customerMatch && dateMatch && categoryMatch;
    });
  }, [sales, search, filters]);

  const consolidatedSales = useMemo(() => {
    const term = search?.toLowerCase() || "";

    // 1. Get filtered retail sales
    const retailSales = filteredSales.map(s => {
      const items = s.items || [];
      const itemSummary = items.map(it => {
        const name = typeof it.item === 'object' ? (it.item?.name || it.item?.itemName || "") : (it.itemName || "");
        return `${name} (x${it.qty})`;
      }).join(", ");
      const totalQty = items.reduce((sum, it) => sum + (Number(it.qty) || 0), 0);

      let extractedVerId = s.verveId || s.customId || s.customerId || s.projectCustomId || '';
      if (!extractedVerId && typeof s.customer === 'string') {
        const match = s.customer.match(/^([A-Z0-9_-]+)\s*-\s*/);
        if (match && match[1]) extractedVerId = match[1];
      }
      if (!extractedVerId && typeof s.customerPhone === 'string' && s.customerPhone.startsWith('ID: ')) {
        extractedVerId = s.customerPhone.replace('ID: ', '').trim();
      }

      return {
        ...s,
        recordType: 'sale',
        saleType: s.saleType || 'Direct Sale',
        verveId: extractedVerId || '—',
        refNo: s.invoiceNo,
        itemSummary,
        totalQty
      };
    });

    // 2. Get dispatched/delivered Material Requests
    const dispatchedRequests = materialRequests
      .filter(req => {
        const status = (req.status || '').toLowerCase();
        return ['dispatched', 'delivered', 'partially_delivered', 'completed'].includes(status);
      })
      .map(req => {
        const proj = req.project || req.projectDetails || projectsLocal.find(p =>
          (p._id?.toString() === req.projectId?.toString()) ||
          (p.id?.toString() === req.projectId?.toString()) ||
          (p.projectId === req.projectId) ||
          (p._id?.toString() === req.project?._id?.toString()) ||
          (p.projectId === req.project?.projectId)
        );

        const customerName = proj?.customerName || proj?.customer?.name || req.customerName || req.projectDetails?.customerName || "Project Site";
        const customerId = proj?.verveId || proj?.customerCustomId || proj?.customId || proj?.leadId?.verveId || proj?.leadId?.customId || proj?.leadRef?.verveId || proj?.leadRef?.customId || req.projectCustomId || req.customerCustomId || req.verveId || "";
        const customerPhoneStr = req.customerPhone || proj?.phoneNumber || proj?.customerPhone || proj?.leadId?.phone || proj?.leadContact?.phone || "";

        // Calculate estimated value and summary
        let totalValue = 0;
        const items = req.items || req.originalData?.items || [];
        const itemSummary = items.map(it => {
          const itemData = it.item || {};
          const name = (itemData.name || itemData.itemName || it.itemName || it.name || "").trim();
          const qty = Number(it.deliveredQty || it.qty || it.quantity || 0);
          const price = Number(it.rate || it.unitPrice || itemData.unitPrice || itemData.rate || itemData.price || itemData.purchasePrice || 0);
          totalValue += (qty * price);
          return `${name} (x${qty})`;
        }).join(", ");
        const totalQty = items.reduce((sum, it) => sum + Number(it.deliveredQty || it.qty || it.quantity || 0), 0);

        return {
          _id: req._id,
          refNo: (req._id || "").toString().slice(-6).toUpperCase(),
          invoiceNo: req.invoiceNo || "—",
          date: req.dispatchedAt || req.createdAt,
          customer: customerName,
          customerPhone: customerId ? `ID: ${customerId}` : "",
          netPayable: totalValue || req.totalAmount || req.amount || 0,
          status: req.status === 'delivered' || req.status === 'fulfilled' ? 'Paid' : 'Partial',
          displayStatus: req.status,
          recordType: 'material_request',
          saleType: req.saleType || 'Project Sale',
          verveId: customerId || '—',
          itemSummary,
          totalQty,
          projectId: req.projectId,
          projectCustomId: customerId,
          invoiceNo: req.invoiceNo || req.requestNo || req.id || req._id?.toString().slice(-6).toUpperCase(),
          originalRequest: req
        };
      })
      .filter(r => {
        const searchMatch = !term || 
          r.customer.toLowerCase().includes(term) ||
          r.invoiceNo.toLowerCase().includes(term) ||
          r.customerPhone.toLowerCase().includes(term);

        const customerMatch = !filters.customer || filters.customer === 'all' || 
          r.customer.toLowerCase() === filters.customer.toLowerCase();

        let dateMatch = true;
        const d = new Date(r.date);
        if (filters.date && filters.date !== 'all') {
          const today = new Date();
          if (filters.date === 'today') {
            dateMatch = d.toDateString() === today.toDateString();
          } else if (filters.date === 'this_week') {
            const currentDay = today.getDay();
            const diff = today.getDate() - currentDay + (currentDay === 0 ? -6 : 1);
            const start = new Date(today.setDate(diff));
            start.setHours(0,0,0,0);
            dateMatch = d >= start;
          } else if (filters.date === 'this_month') {
            dateMatch = d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear();
          } else if (filters.date === 'this_year') {
            dateMatch = d.getFullYear() === today.getFullYear();
          }
        } else if (filters.fromDate || filters.toDate) {
          if (filters.fromDate) {
            const start = new Date(filters.fromDate);
            start.setHours(0,0,0,0);
            if (d < start) dateMatch = false;
          }
          if (filters.toDate) {
            const end = new Date(filters.toDate);
            end.setHours(23,59,59,999);
            if (d > end) dateMatch = false;
          }
        }

        let categoryMatch = true;
        if (filters.category && filters.category !== 'all') {
          categoryMatch = (r.originalRequest?.items || []).some(it => {
            const cat = typeof it.item?.category === 'object' ? (it.item.category?._id || it.item.category?.categoryName || it.item.category?.title) : (it.item?.category || it.category);
            return String(cat) === filters.category || String(it.item?.category) === filters.category;
          });
        }
          
        return searchMatch && customerMatch && dateMatch && categoryMatch;
      });

    return [...retailSales, ...dispatchedRequests].sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [filteredSales, materialRequests, projectsLocal, search, filters]);

  const filteredPurchaseReturns = useMemo(() => {
    const term = search?.toLowerCase() || "";
    return purchaseReturns.filter(r => {
      const vendorName = typeof r.vendor === 'object' ? (r.vendor?.name || r.vendor?.title || "") : (r.vendor || r.supplier || "");
      const searchMatch = !term || (
        (r.returnNo || "").toLowerCase().includes(term) ||
        (r.invoiceNo || "").toLowerCase().includes(term) ||
        vendorName.toLowerCase().includes(term)
      );

      const vendorId = r.vendor?._id ? r.vendor._id.toString() : r.vendor?.toString();
      const supplierMatch = filters.supplier === "all" || vendorId === filters.supplier;

      return searchMatch && supplierMatch;
    });
  }, [purchaseReturns, search, filters]);

  const filteredSaleReturns = useMemo(() => {
    const term = search?.toLowerCase() || "";
    return saleReturns.filter(r => {
      const customerName = typeof r.customer === "object" ? (r.customer?.name || r.customer?.title || "") : (r.customer || "");
      const searchMatch = !term || (
        (r.invoiceNo || "").toLowerCase().includes(term) ||
        customerName.toLowerCase().includes(term)
      );
      return searchMatch;
    });
  }, [saleReturns, search, filters]);

  const totalValue = useMemo(() => activeInventory.reduce((s, i) => s + (Number(i.availableQty) || 0) * (Number(i.unitPrice || i.rate) || 0), 0), [activeInventory]);

  const distributedMap = useMemo(() => {
    const map = {};
    materialRequests.forEach(req => {
      // Only count items that have actually left the warehouse
      if (['dispatched', 'delivered', 'partially_delivered'].includes(req.status)) {
        req.items?.forEach(it => {
          const itemData = it.item || {};
          const name = (itemData.name || itemData.itemName || '').trim();
          if (name) map[name] = (map[name] || 0) + (it.issuedQuantity || it.quantity || it.qty || 0);
        });
      }
    });
    sales.forEach(sale => {
      if (sale.status === 'confirmed') {
        sale.items?.forEach(it => {
          const itemData = it.item || {};
          const name = (itemData.name || itemData.itemName || '').trim();
          if (name) map[name] = (map[name] || 0) + (it.qty || 0);
        });
      }
    });
    
    // Subtract Material Returns (Site Returns)
    materialReturns.forEach(ret => {
      if (['received', 'restocked'].includes(ret.status)) {
        ret.items?.forEach(it => {
          const itemData = it.item || {};
          const name = (itemData.name || itemData.itemName || '').trim();
          if (name) map[name] = (map[name] || 0) - (it.qty || 0);
        });
      }
    });

    // Subtract Sale Returns (Customer Returns)
    saleReturns.forEach(ret => {
      if (ret.status === 'confirmed') {
        ret.items?.forEach(it => {
          const itemData = it.item || {};
          const name = (itemData.name || itemData.itemName || '').trim();
          if (name) map[name] = (map[name] || 0) - (it.qty || 0);
        });
      }
    });

    return map;
  }, [materialRequests, sales, materialReturns, saleReturns]);

  const statsBreakdown = useMemo(() => {
    const stockMap = {};
    const valueMap = {};
    const lowStockMap = {};

    allAggregatedItems.forEach(item => {
      const c = (typeof item.category === 'object' ? (item.category?.title || item.category?.name) : item.category) || "Other";
      stockMap[c] = (stockMap[c] || 0) + item.availableQty;
      valueMap[c] = (valueMap[c] || 0) + (item.availableQty * (item.unitPrice || 0));
      if (item.availableQty <= item.minQty) {
        lowStockMap[c] = (lowStockMap[c] || 0) + 1;
      }
    });

    return {
      stock: Object.entries(stockMap).map(([name, value]) => ({ name, value })),
      value: Object.entries(valueMap).map(([name, value]) => ({ name, value })),
      lowStock: Object.entries(lowStockMap).map(([name, value]) => ({ name, value }))
    };
  }, [allAggregatedItems]);

  const totalUnits = useMemo(() => activeInventory.reduce((sum, i) => sum + (Number(i.availableQty) || 0), 0), [activeInventory]);
  const averageValuePerItem = useMemo(() => (allAggregatedItems.length > 0 ? totalValue / allAggregatedItems.length : 0), [totalValue, allAggregatedItems]);

  const requestStats = useMemo(() => {
    const total = materialRequests.length;
    const pending = materialRequests.filter(r => ['pending', 'pending_store', 'pending_owner_approval'].includes(r.status)).length;
    const moving = materialRequests.filter(r => ['dispatched', 'owner_approved', 'partially_delivered'].includes(r.status)).length;
    const completed = materialRequests.filter(r => ['delivered', 'fulfilled'].includes(r.status)).length;
    return { total, pending, moving, completed };
  }, [materialRequests]);

  const returnStats = useMemo(() => {
    const siteCount = materialReturns.length;
    const purchaseCount = purchaseReturns.length;
    const saleCount = saleReturns.length;
    const total = siteCount + purchaseCount + saleCount;

    // Helper to get amount from any return type
    const getAmt = (r, type) => {
      if (type === 'site') return Number(r.totalValue || r.amount || 0);
      return Number(r.amount || r.totalAmount || r.refundAmount || r.netPayable || 0);
    };

    const siteValue = materialReturns.reduce((sum, r) => sum + getAmt(r, 'site'), 0);
    const purchaseValue = purchaseReturns.reduce((sum, r) => sum + getAmt(r), 0);
    const saleValue = saleReturns.reduce((sum, r) => sum + getAmt(r), 0);
    const totalValue = siteValue + purchaseValue + saleValue;

    return { total, siteCount, purchaseCount, saleCount, totalValue };
  }, [materialReturns, purchaseReturns, saleReturns]);

  const openStock = useMemo(() => {
    const flatOpenStock = [];
    (purchasesState || []).forEach(inv => {
      if (inv.serviceType && inv.serviceType !== activeTab) return;
      if (!(inv.isOpenStock || (inv.invoiceNo && inv.invoiceNo.startsWith('OPEN-STOCK')) || (typeof inv.vendor === 'string' && inv.vendor === 'Opening Stock'))) return;
      
      const items = inv.items || [];
      items.forEach(it => {
        flatOpenStock.push({
          ...inv,
          ...it,
          id: `${inv._id}-${it._id || Math.random()}`,
          name: it.item?.itemName || it.item?.name || it.itemName || it.name,
          itemCode: it.item?.itemCode || it.itemCode || it.code,
          vendorName: typeof inv.vendor === 'object' ? inv.vendor?.name : inv.vendor,
          supplier: typeof inv.vendor === 'object' ? inv.vendor?.name : inv.vendor,
          itemDate: inv.invoiceDate || inv.createdAt,
          quantity: it.quantity || 0,
          availableQty: it.availableQty !== undefined ? it.availableQty : (it.quantity || 0),
          unitPrice: it.rate || it.purchaseRate || it.unitPrice || 0,
          categoryDisplay: typeof it.category === 'object' ? (it.category?.categoryName || it.category?.title) : (typeof it.item?.category === 'object' ? (it.item.category?.categoryName || it.item.category?.title) : (it.category || it.item?.category)),
          storeDisplay: typeof inv.store === 'object' ? (inv.store?.storeName || inv.store?.name) : inv.store,
          unitDisplay: typeof it.units === 'object' ? it.units?.name : (typeof it.item?.units === 'object' ? it.item.units?.name : (it.units || it.unit || it.item?.unit))
        });
      });
    });
    return flatOpenStock;
  }, [purchasesState, activeTab]);

  const openStockStats = useMemo(() => {
    const totalAmount = openStock.reduce((sum, i) => sum + (Number(i.availableQty || i.quantity) * (Number(i.unitPrice || i.rate) || 0)), 0);
    const totalItems = openStock.reduce((sum, i) => sum + (Number(i.availableQty || i.quantity) || 0), 0);
    const entriesCount = openStock.length;

    const categoryMap = {};
    openStock.forEach(i => {
      const c = i.categoryDisplay || "Other";
      categoryMap[c] = (categoryMap[c] || 0) + (Number(i.availableQty || i.quantity) * (Number(i.unitPrice || i.rate) || 0));
    });

    return {
      totalAmount,
      totalItems,
      entriesCount,
      distribution: Object.entries(categoryMap).map(([name, value]) => ({ name, value }))
    };
  }, [openStock]);



  const saleStats = useMemo(() => {
    const totalRevenue = consolidatedSales.reduce((sum, s) => sum + (Number(s.netPayable) || 0), 0);
    const totalQty = consolidatedSales.reduce((sum, s) => sum + (Number(s.totalQty) || 0), 0);
    const ordersCount = consolidatedSales.length;
    const customers = new Set(consolidatedSales.map(s => s.customer).filter(Boolean));

    const customerMap = {};
    consolidatedSales.forEach(s => {
      const c = s.customer || "Other";
      customerMap[c] = (customerMap[c] || 0) + (Number(s.netPayable) || 0);
    });

    return {
      totalRevenue,
      totalQty,
      ordersCount,
      customersCount: customers.size,
      distribution: Object.entries(customerMap).map(([name, value]) => ({ name, value }))
    };
  }, [consolidatedSales]);

  const allFormattedReturns = useMemo(() => {
    return [
      ...materialReturns.map(r => {
        const req = materialRequests.find(m => m._id?.toString() === r.materialRequestId?.toString());
        const items = r.items || [];
        const enrichedItems = items.map(it => {
          const itemPrice = Number(
            it.rate || it.unitPrice || it.price ||
            it.inventoryId?.purchaseRate ||
            it.inventoryId?.unitPrice ||
            it.item?.unitPrice || it.item?.rate || it.item?.price ||
            inventory.find(i => i._id?.toString() === (it.item?._id || it.item || it.inventoryId?._id || it.inventoryId)?.toString())?.unitPrice || 
            inventory.find(i => i._id?.toString() === (it.item?._id || it.item || it.inventoryId?._id || it.inventoryId)?.toString())?.rate ||
            0
          );
          return {
             ...it,
             quantity: Number(it.qty || it.quantity || 0),
             qty: Number(it.qty || it.quantity || 0),
             unit: it.unit || it.inventoryId?.item?.units?.name || it.item?.units?.name || "NOS",
             rate: itemPrice,
             unitPrice: itemPrice
          };
        });

        const itemNames = enrichedItems.map(it => it.itemName || it.item?.itemName || it.item?.name || it.inventoryId?.item?.itemName || it.inventoryId?.item?.name || "Unknown Item");
        const itemSummary = itemNames.length > 2 
          ? `${itemNames.slice(0, 2).join(", ")} + ${itemNames.length - 2} more` 
          : itemNames.join(", ");

        const calculatedValue = enrichedItems.reduce((sum, it) => sum + (it.qty * it.rate), 0);

        return {
          ...r,
          returnType: 'Site',
          rawType: 'site',
          date: r.createdAt,
          party: r.projectDetails?.customerName || 'Site',
          amount: Number(r.totalAmount || r.totalValue || calculatedValue || 0),
          itemQuantity: enrichedItems.reduce((sum, it) => sum + it.qty, 0),
          itemSummary: itemSummary || "No Items",
          items: enrichedItems,
          reason: r.notes || r.reason || r.remarks || r.description || "General Return",
          invoiceNo: r.displayProjectId || r.projectDisplayId || r.project?.leadContact?.customId || r.project?.leadId?.customId || r.project?.leadRef?.customId || r.project?.projectId || r.invoiceNo || r.refInvoice || (req?._id?.toString().slice(-6).toUpperCase()) || "—",
          projectId: r.displayProjectId || r.projectDisplayId || r.project?.leadContact?.customId || r.project?.leadId?.customId || r.project?.leadRef?.customId || r.project?.projectId || "—",
          returnId: r.returnId || r._id?.toString().slice(-6).toUpperCase(),
          originalData: r
        };
      }),
      ...purchaseReturns.map(r => {
        const purchaseId = (r.purchaseRef?._id || r.purchaseRef)?.toString();
        const original = (inventory || []).find(p => p._id?.toString() === purchaseId);
        const invNo = original?.invoiceNo || original?.billNo || r.purchaseRef?.invoiceNo || r.purchaseRef?.billNo || r.invoiceNo || r.billNo || r.returnNo || "—";
        const items = r.items || [];
        const itemNames = items.map(it => it.itemName || it.item?.itemName || it.item?.name || it.inventoryId?.item?.itemName || it.inventoryId?.item?.name || "Unknown Item");
        const itemSummary = itemNames.length > 2 
          ? `${itemNames.slice(0, 2).join(", ")} + ${itemNames.length - 2} more` 
          : itemNames.join(", ");

        return {
          ...r,
          returnType: 'Purchase',
          rawType: 'purchase',
          date: r.date || r.returnDate,
          party: typeof r.vendor === 'object' ? (r.vendor?.name || r.vendor?.title) : (r.vendor || r.supplier || 'Vendor'),
          amount: Number(r.amount || r.totalAmount || r.refundAmount || 0),
          receivedAmount: Number(r.refundAmount || 0),
          itemQuantity: items.reduce((sum, it) => sum + (Number(it.qty || it.quantity) || 0), 0),
          itemSummary: itemSummary || "No Items",
          items: items.map(it => ({
             ...it,
             quantity: Number(it.qty || it.quantity || 0),
             qty: Number(it.qty || it.quantity || 0),
             unit: it.unit || it.item?.units?.name || "NOS"
          })),
          reason: r.reason || r.returnType || r.remarks || r.description || "General Return",
          invoiceNo: invNo,
          returnId: r.returnId || r.returnNo || r._id?.toString().slice(-6).toUpperCase(),
          originalData: r
        };
      }),
      ...saleReturns.map(r => {
        const saleId = (r.saleRef?._id || r.saleRef)?.toString();
        const original = (consolidatedSales || []).find(s => s._id?.toString() === saleId);
        const invNo = original?.invoiceNo || r.saleRef?.invoiceNo || r.invoiceNo || r.returnNo || "—";
        const items = r.items || [];
        const itemNames = items.map(it => it.itemName || it.item?.itemName || it.item?.name || it.inventoryId?.item?.itemName || it.inventoryId?.item?.name || "Unknown Item");
        const itemSummary = itemNames.length > 2 
          ? `${itemNames.slice(0, 2).join(", ")} + ${itemNames.length - 2} more` 
          : itemNames.join(", ");

        return {
          ...r,
          returnType: 'Sale',
          rawType: 'sale',
          date: r.date || r.returnDate,
          party: typeof r.customer === 'object' ? (r.customer?.name || r.customer?.title) : (r.customer || 'Customer'),
          amount: Number(r.amount || r.totalAmount || r.refundAmount || r.netPayable || 0),
          itemQuantity: items.reduce((sum, it) => sum + (Number(it.qty || it.quantity) || 0), 0),
          itemSummary: itemSummary || "No Items",
          items: items.map(it => ({
             ...it,
             quantity: Number(it.qty || it.quantity || 0),
             qty: Number(it.qty || it.quantity || 0),
             unit: it.unit || it.item?.units?.name || "NOS"
          })),
          reason: r.reason || r.returnType || r.remarks || r.description || "General Return",
          invoiceNo: invNo,
          returnId: r.returnId || r.returnNo || r._id?.toString().slice(-6).toUpperCase(),
          originalData: r
        };
      })
    ];
  }, [materialReturns, purchaseReturns, saleReturns, materialRequests, projectsLocal, inventory, consolidatedSales]);

  const rawSubTabReturns = useMemo(() => {
    if (returnsSubTab === 'master') return allFormattedReturns;
    if (returnsSubTab === 'site') return allFormattedReturns.filter(r => r.rawType === 'site' || r.returnType === 'Site');
    if (returnsSubTab === 'purchases') return allFormattedReturns.filter(r => r.rawType === 'purchase' || r.returnType === 'Purchase');
    if (returnsSubTab === 'sales') return allFormattedReturns.filter(r => r.rawType === 'sale' || r.returnType === 'Sale');
    if (returnsSubTab === 'damaged') {
      return allFormattedReturns.filter(r => {
        if (r.originalData?.damageReport && r.originalData.damageReport.length > 0) return true;
        if (r.originalData?.items && r.originalData.items.some(item => item.damagedQty && item.damagedQty > 0)) return true;
        if (r.items && r.items.some(item => item.damagedQty && item.damagedQty > 0)) return true;
        if (r.originalData?.notes?.includes('Damage Report:') || r.notes?.includes('Damage Report:')) return true;
        return false;
      });
    }
    return [];
  }, [allFormattedReturns, returnsSubTab]);

  const activeReturnFilterOptions = useMemo(() => {
    const categories = new Set();
    const parties = new Set();
    rawSubTabReturns.forEach(r => {
      if (r.party) parties.add(r.party);
      (r.items || []).forEach(it => {
        const cat = typeof it.item === 'object' ? it.item?.category : (it.category || it.item?.itemName);
        if (cat) categories.add(cat);
      });
    });
    return {
      categories: Array.from(categories).sort(),
      parties: Array.from(parties).sort()
    };
  }, [rawSubTabReturns]);

  const activeSaleFilterOptions = useMemo(() => {
    const partiesMap = new Map();
    consolidatedSales.forEach(s => {
      const customerName = (s.customer || "").trim();
      if (customerName) {
        if (!partiesMap.has(customerName.toLowerCase())) {
          partiesMap.set(customerName.toLowerCase(), {
            type: 'customer',
            customerName: customerName,
            name: customerName,
            value: customerName
          });
        }
      }
    });
    return {
      parties: Array.from(partiesMap.values()).sort((a,b) => a.customerName.localeCompare(b.customerName))
    };
  }, [consolidatedSales]);

  const consolidatedReturns = useMemo(() => {
    return rawSubTabReturns.filter(r => {
      const term = search?.toLowerCase() || "";
      const searchMatch = !term || (
        (r.returnId || "").toLowerCase().includes(term) ||
        (r.party || "").toLowerCase().includes(term) ||
        (r.reason || "").toLowerCase().includes(term) ||
        (r.invoiceNo || "").toLowerCase().includes(term)
      );

      const supplierMatch = filters.supplier === "all" || r.party === filters.supplier;
      const categoryMatch = filters.category === "all" || (
        (r.items && r.items.some(it => {
          const cat = typeof it.item === 'object' ? it.item?.category : (it.category || it.item?.itemName);
          return cat === filters.category;
        }))
      );

      return searchMatch && supplierMatch && categoryMatch;
    }).sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [rawSubTabReturns, search, filters]);

  const activeReturnStats = useMemo(() => {
    const total = consolidatedReturns.length;
    const items = consolidatedReturns.reduce((sum, r) => sum + (r.itemQuantity || 0), 0);
    const amount = consolidatedReturns.reduce((sum, r) => sum + (r.amount || 0), 0);

    return { total, type: returnsSubTab.charAt(0).toUpperCase() + returnsSubTab.slice(1), items, amount };
  }, [consolidatedReturns, returnsSubTab]);

  const restock = async () => {
    if (!restockItem || !restockQty) return;
    try {
      const newQty = restockItem.availableQty + Number(restockQty);
      await services.updateInventoryItem(restockItem._id, {
        ...restockItem,
        availableQty: newQty,
        serviceType: restockItem.serviceType || activeTab
      });
      setInventoryState(p => p.map(i => i._id === restockItem._id ? { ...i, availableQty: newQty } : i));
      toast.success(`${restockItem.name} restocked!`);
      setRestockItem(null); setRestockQty("");
    } catch (err) {
      toast.error("Restock failed");
    }
  };

  const addItem = async (passedData = null) => {
    const isEvent = passedData && typeof passedData.preventDefault === 'function';
    const currentForm = (passedData && !isEvent && Object.keys(passedData).length > 0) ? passedData : form;

    // If we have items in the list, it's a multi-item purchase
    const isMultiItem = currentForm.items && currentForm.items.length > 0;

    if (!isMultiItem && !currentForm.name) return toast.error("Item name is required or add at least one item to list");

    try {
      setLoading(true);
      // Sanitize numeric fields before sending to API
      const numericFields = [
        'availableQty', 'minQty', 'unitPrice', 'rate', 'quantity',
        'bvPoints', 'taxP', 'baseAmount', 'discP', 'discount',
        'taxable', 'igst', 'sgst', 'cgst', 'gst', 'taxAmount',
        'netBill', 'chargeAmount', 'adjustments', 'netPayment',
        'paidAmount'
      ];

      const sanitizeItem = (itemData) => {
        const sanitized = { ...itemData };
        numericFields.forEach(field => {
          if (sanitized[field] !== undefined) {
            sanitized[field] = Number(sanitized[field]) || 0;
          }
        });
        return sanitized;
      };

      if (isMultiItem) {
        // Handle Multi-item Purchase
        const itemsTotal = form.items.reduce((sum, it) => sum + (Number(it.netPayment) || 0), 0);
        const taxableTotal = form.items.reduce((sum, it) => sum + (Number(it.taxable) || 0), 0);
        const taxTotal = form.items.reduce((sum, it) => sum + (Number(it.taxAmount) || 0), 0);
        const discountTotal = form.items.reduce((sum, it) => sum + (Number(it.discount) || 0), 0);

        const payload = {
          vendor: form.supplier || "",
          store: form.store || "",
          billNo: form.invoiceNo || "",
          invoiceNo: form.invoiceNo || "",
          date: form.invoiceDate || new Date().toISOString().split('T')[0],
          serviceType: activeTab || "Verve",
          items: form.items.map(it => ({
            item: it.item || it._id || "",
            name: it.name || it.itemName || "",
            qty: Number(it.quantity) || 1,
            initialQty: Number(it.quantity) || 1,
            availableQty: Number(it.quantity) || 1,
            rate: Number(it.rate) || 0,
            freeQty: Number(it.freeQty) || 0,
            unit: it.unit || "piece",
            taxPercent: Number(it.taxP) || 0,
            taxAmount: Number(it.taxAmount) || 0,
            amount: Number(it.taxable) || 0,
            discountPercent: Number(it.discP) || 0
          })),
          totalDiscount: discountTotal,
          taxableAmount: taxableTotal,
          totalTax: taxTotal,
          netBill: itemsTotal,
          adjustment: (Number(currentForm.adjustments) || 0) + (Number(currentForm.chargeAmount) || 0),
          netPayable: itemsTotal + (Number(currentForm.adjustments) || 0) + (Number(currentForm.chargeAmount) || 0),
          paidAmount: Number(currentForm.netPayment) || 0,
          paymentMode: 'bank',
          isOpenStock: currentForm.invoiceNo === 'OPEN-STOCK' || currentForm.isOpenStock === true
        };

        if (payload.isOpenStock && payload.invoiceNo === 'OPEN-STOCK') {
          payload.invoiceNo = `OPEN-STOCK-${new Date().getTime().toString().slice(-6)}-${Math.floor(Math.random() * 1000)}`;
          payload.billNo = payload.invoiceNo;
        }

        // If vendor and store are strings, we MUST find IDs
        const vendorObj = (suppliers || []).find(s => (s.name || s) === currentForm.supplier || s._id === currentForm.supplier);
        if (vendorObj && vendorObj._id) payload.vendor = vendorObj._id;
        else payload.vendor = currentForm.supplier;

        const storeObj = (stores || []).find(st => (st.title || st.storeName || st.name || st) === currentForm.store || st._id === currentForm.store);
        if (storeObj && storeObj._id) payload.store = storeObj._id;

        const res = await services.createPurchase(payload);
        await refreshAll();
        toast.success("Purchase recorded successfully!");
      } else {
        // Handle Single-item Entry (Existing Behavior)
        const sanitizedForm = sanitizeItem(currentForm);
        if (editingItem) {
          const res = await services.updateInventoryItem(editingItem._id, sanitizedForm);
          const newItem = res.data?.data || res.data;
          setInventoryState(p => p.map(i => i._id === editingItem._id ? newItem : i));
          toast.success("Item updated!");
        } else {
          const res = await services.addItem({ ...sanitizedForm, serviceType: activeTab });
          const newItem = res.data?.data || res.data;
          setInventoryState(p => [newItem, ...p]);
          toast.success("Item added!");
        }
      }

      setAddOpen(false);
      setPurchaseOpen(false);
      setOpenStockOpen(false);
      setEditingItem(null);
      setForm(services.getEmptyItem(activeTab));
      refreshAll();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || (editingItem ? "Failed to update" : "Failed to add");
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const addItemToList = () => {
    if (!form.name) return toast.error("Item name is required to add to list");
    if (!form.quantity || form.quantity <= 0) return toast.error("Quantity must be greater than 0");

    const newItem = {
      ...form,
      id: Date.now(), // Temporary ID for UI tracking
      rowTotal: form.netBill, // Only store the item's own net bill in the row
      item: masterItems.find(mi => mi.itemName === form.name)?._id || form._id,
      amount: form.taxable // Backend expects 'amount' as taxable base
    };

    // Allow new items to be passed to backend for on-the-fly creation

    setForm(prev => {
      const updatedItems = [...(prev.items || []), newItem];
      // Calculate total net payment for all items
      const totalNetPayment = updatedItems.reduce((sum, it) => sum + (Number(it.rowTotal) || 0), 0);

      return {
        ...prev,
        items: updatedItems,
        netPayment: totalNetPayment + (Number(prev.adjustments) || 0),
        // Reset item-specific fields for next entry
        name: "",
        rate: 0,
        quantity: 1,
        baseAmount: 0,
        taxP: 0,
        taxAmount: 0,
        netBill: 0,
        sku: "",
        itemCode: "",
        barcode: "",
        isUnique: false,
        hasBatchExpiry: false,
        _id: "",
        category: "",
        unit: "",
        brand: "",
        itemType: "",
        hsnCode: "",
        uniqueName: "",
        description: ""
      };
    });
    toast.success(`${form.name} added to list`);
  };

  const removeItemFromList = (tempId) => {
    setForm(prev => {
      const updatedItems = prev.items.filter(it => it.id !== tempId);
      const totalNetPayment = updatedItems.reduce((sum, it) => sum + (Number(it.rowTotal) || 0), 0);
      return {
        ...prev,
        items: updatedItems,
        netPayment: totalNetPayment + (Number(prev.adjustments) || 0) + (Number(prev.chargeAmount) || 0)
      };
    });
  };

  const editItemInList = (tempId) => {
    const itemToEdit = form.items.find(it => it.id === tempId);
    if (!itemToEdit) return;

    setForm(prev => {
      const updatedItems = prev.items.filter(it => it.id !== tempId);
      const totalNetPayment = updatedItems.reduce((sum, it) => sum + (Number(it.rowTotal) || 0), 0);
      return {
        ...itemToEdit, // Load item back into form
        items: updatedItems, // Preserve remaining items
        netPayment: totalNetPayment + (Number(prev.adjustments) || 0) + (Number(prev.chargeAmount) || 0)
      };
    });
    toast.success(`${itemToEdit.name} moved to form for editing`);
  };

  const handleAddNewItem = () => {
    setEditingItem(null);
    setPurchaseOpen(false);
    setForm({
      ...services.getEmptyItem(activeTab),
      invoiceDate: new Date().toISOString().split('T')[0],
      businessUnit: "VERVE KITCHENS"
    });
    setAddOpen(true);
  };

  const handleNewPurchase = () => {
    setEditingItem(null);
    setAddOpen(false);
    setOpenStockOpen(false);
    setForm({
      ...services.getEmptyItem(activeTab),
      invoiceDate: new Date().toISOString().split('T')[0],
      businessUnit: "VERVE KITCHENS"
    });
    setPurchaseOpen(true);
  };

  const handleNewOpenStock = () => {
    setEditingItem(null);
    setAddOpen(false);
    setPurchaseOpen(false);
    setForm({
      ...services.getEmptyItem(activeTab),
      invoiceDate: new Date().toISOString().split('T')[0],
      businessUnit: "VERVE KITCHENS",
      invoiceNo: "OPEN-STOCK",
      supplier: "Opening Stock"
    });
    setOpenStockOpen(true);
  };

  const handleSelectExistingItem = (itemOrId) => {
    if (!itemOrId) {
      setEditingItem(null);
      setForm(prev => ({ ...prev, name: "", sku: "", itemCode: "", category: "", unit: "" }));
      return;
    }
    
    // Support passing full item object (like Master Items) or just ID
    const passedId = typeof itemOrId === 'object' ? itemOrId._id : itemOrId;
    let item = inventory.find(i => i._id === passedId);
    
    // Fallback to the object itself if it's a Master item not in inventory
    if (!item && typeof itemOrId === 'object') {
      item = itemOrId;
    }

    if (item) {
      setEditingItem(item);

      // If the selected item is from the inventory (flattened line item), the master details are inside .item
      const sourceItem = item.item || item;

      const extractId = (val) => {
        if (!val) return "";
        if (typeof val === "string") return val;
        return val._id || val.id || val.value || val.name || val.title || val.categoryName || val.brandName || "";
      };

      const itemName = item.name || item.itemName || sourceItem.name || sourceItem.itemName || "";
      const itemCategory = extractId(sourceItem.category);
      const itemUnit = extractId(sourceItem.unit) || extractId(sourceItem.units);
      const itemBrand = extractId(sourceItem.brand);

      // Extract itemType cleanly from master details (capitalized fallback)
      let itType = item.itemType || sourceItem.itemType || form.itemType || "";
      if (itType && typeof itType === 'string') {
        itType = itType.charAt(0).toUpperCase() + itType.slice(1).toLowerCase();
      }

      setForm(prev => ({
        ...prev,
        _id: item._id, // Keep the specific line item or master ID
        name: itemName,
        sku: item.sku || sourceItem.sku || "",
        itemCode: item.itemCode || sourceItem.itemCode || "",
        category: itemCategory,
        unit: itemUnit,
        brand: itemBrand,
        itemType: itType,
        hsnCode: item.hsnSacCode || sourceItem.hsnSacCode || prev.hsnCode || "",
        barcode: item.barcode || sourceItem.barcode || prev.barcode || "",
        isUnique: item.isUnique || sourceItem.isUnique || false,
        uniqueName: item.uniqueName || sourceItem.uniqueName || "",
        hasBatchExpiry: item.hasBatch || sourceItem.hasBatch || false,
        description: item.description || sourceItem.description || prev.description || "",
        invoiceNo: prev.invoiceNo,
        invoiceDate: prev.invoiceDate,
        businessUnit: prev.businessUnit,
        store: prev.store,
        baseAmount: prev.baseAmount,
        discount: prev.discount,
        igst: prev.igst,
        sgst: prev.sgst,
        cgst: prev.cgst,
        chargeAmount: prev.chargeAmount,
        adjustments: prev.adjustments,
        availableQty: prev.availableQty || 0
      }));
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    // Merge with defaults to prevent uncontrolled to controlled input warnings
    setForm({ ...services.getEmptyItem(activeTab), ...item });
    setAddOpen(true);
  };


  const handleEditOpenStock = (item) => {
    setEditingItem(item);
    setForm({
      ...services.getEmptyItem(activeTab),
      ...item,
      // Ensure quantity and rate are copied for calculations
      quantity: Number(item.availableQty || item.quantity || 0),
      rate: Number(item.unitPrice || item.rate || 0)
    });
    setOpenStockOpen(true);
  };

  const handleViewPurchases = (item) => {
    const itemName = item.name || item.itemName;

    // Check if there are any actual purchases for this item in the inventory
    // We search across full inventory, matching by name
    const itemRecords = inventory.filter(i => (i.name === itemName || i.itemName === itemName));

    const hasPurchases = itemRecords.some(i =>
      i.invoiceNo && i.invoiceNo !== 'OPEN-STOCK' && i.supplier !== 'Opening Stock'
    );

    const hasOpenStock = itemRecords.some(i =>
      i.invoiceNo === 'OPEN-STOCK' || i.supplier === 'Opening Stock'
    );

    // If item only has open stock, go to Open Stock tab
    if (hasOpenStock && !hasPurchases) {
      setInventorySubTab("open_stock");
    } else {
      // Default to purchases tab
      setInventorySubTab("purchases");
    }

    setSearch(itemName);
    // Clear filters to ensure results are visible
    setFilters(prev => ({ ...prev, category: "all", supplier: "all", stockStatus: "all" }));
  };

  const deleteItem = async (id) => {
    if (!window.confirm("Are you sure?")) return;
    try {
      await services.deleteItem(id);
      setInventoryState(p => p.filter(i => i._id !== id));
      toast.success("Item deleted!");
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const approveRequest = async (id) => {
    try {
      await services.updateRequestStatus(id, "dispatched");
      setMRState(p => p.map(r => r._id === id ? { ...r, status: "dispatched", dispatchedAt: new Date().toISOString() } : r));
      addNotification({ title: "Materials Dispatched", message: `Request dispatched`, type: "success", serviceType: activeTab });
      toast.success("Request dispatched!");
      setSelReq(null);
    } catch (err) {
      toast.error("Failed to update request");
    }
  };

  const ownerAction = async (id, action, extraData = {}) => {
    try {
      await services.updateRequestStatus(id, action);
      setMRState(p => p.map(r => r._id === id ? { ...r, status: action } : r));
      toast.success(action === "owner_approved" ? "Approved!" : "Rejected!");
      setSelReq(null);
    } catch (err) {
      toast.error("Failed to update request");
    }
  };

  const handleDispatch = async (requestId, dispatchData) => {
    try {
      if (!requestId) throw new Error("Request ID is missing");
      const id = String(requestId);
      await api.patch(`/inventory/material-requests/${id}/status`, dispatchData);
      toast.success("Materials dispatched successfully!");
      fetchMaterialRequests();
      setDispatchReq(null);
    } catch (error) {
      console.error("Dispatch failed:", error.response?.data || error.message);
      toast.error("Failed to dispatch materials");
    }
  };

  const handleDeliver = async (requestId, extraData = {}) => {
    try {
      if (!requestId) throw new Error("Request ID is missing");
      const id = String(requestId);
      await api.patch(`/inventory/material-requests/${id}/status`, { status: "delivered", ...extraData });
      toast.success("Materials marked as delivered!");
      fetchMaterialRequests();
    } catch (error) {
      console.error("Delivery update failed:", error.response?.data || error.message);
      toast.error("Failed to update delivery status");
    }
  };

  const handleConfirmReturn = async (itemOrId, action = 'receive') => {
    try {
      if (typeof itemOrId === 'string') {
        // Legacy/Direct ID: Material Return (Site Return)
        await api.patch(`/inventory/material-returns/${itemOrId}/status`, { status: "received" });
        toast.success("Return items received and added to stock!");
      } else {
        const item = itemOrId;
        // Use the correct return ID from the original data
        const returnId = item.originalData?._id || item._id;

        console.log("[DEBUG] Processing return:", {
          returnType: item.returnType,
          returnId,
          originalData: item.originalData,
          item: item
        });

        const rType = (item.rawType || item.returnType || '').toLowerCase();
        const currentStatus = item.originalData?.status || item.status;

        if (rType === 'sale' || rType === 'site') {
          const endpoint = rType === 'site'
            ? `/inventory/material-returns/${returnId}/status`
            : `/inventory/sale-returns/${returnId}/status`;

          if (action === 'receive') {
            // Check if owner approval is needed before allowing restocking
            const userRole = String(user?.role || '').toLowerCase().trim();
            const isOwner = ['owner', 'admin', 'verve-admin', 'accountant'].includes(userRole);

            if (currentStatus === 'pending') {
              if (!isOwner) {
                toast.error("Return requires owner approval before processing");
                return;
              }
              // Owner can approve the return
              await api.patch(endpoint, { status: "owner_approved" });
              toast.success("Return approved by owner! Please click + to restock items.");
              return; // Don't open restock modal yet, wait for explicit + click
            } else if (currentStatus === 'owner_approved') {
              // Return is approved, now allow restocking
              setRestockReturn(item);
              toast.success("Opening restock form for approved return...");
              return;
            } else if (currentStatus === 'received' || currentStatus === 'restocked') {
              toast.success("Return already processed, opening restock form...");
              setRestockReturn(item);
              return;
            } else {
              // For any other status, try to mark as received
              await api.patch(endpoint, { status: "received" });
              toast.success("Return receipt confirmed!");
              setRestockReturn(item);
              return;
            }
          } else if (action === 'restock') {
            // Check if return is approved before allowing restock
            if (currentStatus === 'pending' && !isOwner) {
              toast.error("Return requires owner approval before restocking");
              return;
            }
            setRestockReturn(item);
            return;
          }
        } else if (rType === 'purchase') {
          // Purchase Return - Open payment capture modal
          setSelectedPRForPayment(item);
          return;
        } else {
          // Fallback for any other types
          const fallbackEndpoint = (item.invoiceNo && !item.supplier)
            ? `/inventory/sale-returns/${returnId}/status`
            : `/inventory/material-returns/${returnId}/status`;

          await api.patch(fallbackEndpoint, { status: "received" });
          toast.success("Return processed!");
        }
      }

      await refreshAll();
      setSelReturn(null); setSelSR(null); setSelPR(null);
    } catch (error) {
      console.error("[CONFIRM-RETURN-ERROR]", error);
      toast.error(error.response?.data?.message || "Failed to process return");
    }
  };

  const finalRestockReturn = async (returnId, adjustedItems = null) => {
    try {
      const item = restockReturn;
      if (!item) return;

      // Use the correct return ID from the original data
      const correctReturnId = item.originalData?._id || returnId;
      const rType = (item.rawType || item.returnType || '').toLowerCase();
      const endpoint = rType === 'site'
        ? `/inventory/material-returns/${correctReturnId}/restock`
        : `/inventory/sale-returns/${correctReturnId}/restock`;

      console.log("[DEBUG] Restocking return:", {
        returnType: item.returnType,
        correctReturnId,
        endpoint,
        adjustedItems
      });

      await api.post(endpoint, { items: adjustedItems });

      toast.success("Items added back to inventory!");
      setRestockReturn(null);
      await refreshAll();
    } catch (err) {
      console.error("[RESTOCK-RETURN-ERROR]", err);
      toast.error(err.response?.data?.message || "Restock failed");
    }
  };

  const handlePurchaseReturnPayment = async (returnData, paymentData) => {
    try {
      const id = returnData.originalData?._id || returnData._id;
      if (!id) throw new Error("Missing return ID");

      await api.patch(`/final-inventory/purchase-returns/${id}/payment`, paymentData);

      toast.success("Payment recorded successfully!");
      setSelectedPRForPayment(null);
      await refreshAll();
    } catch (err) {
      console.error("[PR-PAYMENT-ERROR]", err);
      toast.error(err.response?.data?.message || "Failed to record payment");
    }
  };

  const purchases = useMemo(() => {
    return (purchasesState || []).filter(inv => {
      // Always include Queue items
      if (inv.status === 'Queue' || inv.isQueue) return true;
      // Exclude open stock entries
      if (inv.isOpenStock) return false;
      if (inv.invoiceNo && inv.invoiceNo.startsWith('OPEN-STOCK')) return false;
      if (typeof inv.vendor === 'string' && inv.vendor === 'Opening Stock') return false;
      // Filter by activeTab serviceType, but also show entries without serviceType set
      if (inv.serviceType && inv.serviceType !== activeTab) return false;
      return true;
    });
  }, [purchasesState, activeTab]);



  const filteredPurchases = useMemo(() => {
    const term = search?.toLowerCase() || "";
    return purchases.filter(p => {
      // Resolve vendor display name from object or string
      const vendorName = typeof p.vendor === 'object'
        ? (p.vendor?.vendorName || p.vendor?.name || "")
        : (p.vendor || "");
      const vendorId = typeof p.vendor === 'object' ? p.vendor?._id?.toString() : String(p.vendor || "");

      const singleItemName = p.item ? String(p.item.itemName || p.item.name || p.description || "").toLowerCase() : "";
      const itemsMatch = p.items && Array.isArray(p.items) 
         ? p.items.some(it => String(it.item?.itemName || it.item?.name || it.itemName || it.description || "").toLowerCase().includes(term))
         : false;

      const searchMatch = !term || (
        (p.invoiceNo || p.invoiceNumber || "").toLowerCase().includes(term) ||
        vendorName.toLowerCase().includes(term) ||
        singleItemName.includes(term) ||
        itemsMatch
      );

      const catValue = String(filters.category || "all").toLowerCase().trim();
      let catMatch = catValue === "all" || catValue === "all categories";
      if (!catMatch && p.items && Array.isArray(p.items)) {
         catMatch = p.items.some(it => {
            const itemCat = it.item?.category;
            const cName = String(typeof itemCat === 'object' ? (itemCat?.title || itemCat?.name || itemCat?.categoryName) : (itemCat || "")).toLowerCase().trim();
            const cId = String(typeof itemCat === 'object' ? (itemCat?._id?.toString()) : (itemCat || "")).toLowerCase().trim();
            return cName === catValue || cId === catValue;
         });
      }

      const itemValue = String(filters.item || "all").toLowerCase().trim();
      let itemMatch = itemValue === "all" || itemValue === "all items";
      if (!itemMatch && p.items && Array.isArray(p.items)) {
         itemMatch = p.items.some(it => {
            const iId = String(it.item?._id || it.item || "").toLowerCase().trim();
            const iName = String(it.item?.itemName || it.item?.name || it.item?.title || it.itemName || it.name || "").toLowerCase().trim();
            return iId === itemValue || iName === itemValue;
         });
      }

      const supplierValue = String(filters.supplier || "all").toLowerCase().trim();
      const supplierMatch = supplierValue === "all" || supplierValue === "all vendors" || 
                            vendorName.toLowerCase().trim() === supplierValue || 
                            (vendorId && vendorId.toLowerCase().trim() === supplierValue);

      let dateMatch = true;
      const pDate = new Date(p.invoiceDate || p.date || p.createdAt);
      if (!isNaN(pDate.getTime())) {
         const dS = new Date(pDate.getFullYear(), pDate.getMonth(), pDate.getDate()).getTime();
         
         if (filters.fromDate) {
            const f = new Date(filters.fromDate);
            if (dS < new Date(f.getFullYear(), f.getMonth(), f.getDate()).getTime()) dateMatch = false;
         }
         if (filters.toDate) {
            const t = new Date(filters.toDate);
            if (dS > new Date(t.getFullYear(), t.getMonth(), t.getDate()).getTime()) dateMatch = false;
         }

         if (filters.date && filters.date !== 'all') {
            const now = new Date();
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();

            if (filters.date === 'today' && dS !== today) dateMatch = false;
            if (filters.date === 'this_week') {
               const start = new Date(today);
               start.setDate(start.getDate() - 6);
               if (dS < start.getTime()) dateMatch = false;
            }
            if (filters.date === 'this_month') {
               const start = new Date(today);
               start.setDate(start.getDate() - 30);
               if (dS < start.getTime()) dateMatch = false;
            }
            if (filters.date === 'this_year') {
               const start = new Date(today);
               start.setDate(start.getDate() - 364);
               if (dS < start.getTime()) dateMatch = false;
            }
         }
      }

      return searchMatch && catMatch && itemMatch && supplierMatch && dateMatch;
    });
  }, [purchases, search, filters]);

  const purchaseStats = useMemo(() => {
    const totalAmount = filteredPurchases.reduce((sum, p) => sum + (Number(p.netPayment || p.finalNetPayment || p.totalAmount || p.netBill || 0)), 0);
    const totalItems = filteredPurchases.reduce((sum, p) => {
       return sum + (p.items || []).reduce((itemSum, it) => itemSum + (Number(it.quantity || it.qty || 0)), 0);
    }, 0);
    const transactions = filteredPurchases.length;
    
    const suppliers = new Set();
    filteredPurchases.forEach(p => {
       const vendorName = typeof p.vendor === 'object' ? (p.vendor?.vendorName || p.vendor?.name) : p.vendor;
       if (vendorName) suppliers.add(vendorName);
    });

    return {
      totalAmount,
      totalItems,
      transactions,
      suppliersCount: suppliers.size,
    };
  }, [filteredPurchases]);

  const filteredOpenStock = useMemo(() => {
    const term = search?.toLowerCase() || "";
    return openStock.filter(p => {
      const searchMatch = !term || (
        (p.name || "").toLowerCase().includes(term) ||
        (p.supplier || "").toLowerCase().includes(term)
      );
      
      const catValue = String(filters.category || "all").toLowerCase().trim();
      const itemCat = p.item?.category || p.category;
      const cName = String(typeof itemCat === 'object' ? (itemCat?.title || itemCat?.name || itemCat?.categoryName) : (itemCat || "")).toLowerCase().trim();
      const cId = String(typeof itemCat === 'object' ? (itemCat?._id?.toString()) : (itemCat || "")).toLowerCase().trim();
      const categoryMatch = catValue === "all" || catValue === "all categories" || cName === catValue || cId === catValue;
      
      const supplierValue = String(filters.supplier || "all").toLowerCase().trim();
      const vendorName = p.supplier || "";
      const vendorId = typeof p.vendor === 'object' ? p.vendor?._id?.toString() : String(p.vendor || "");
      const supplierMatch = supplierValue === "all" || supplierValue === "all vendors" || 
                            vendorName.toLowerCase().trim() === supplierValue ||
                            (vendorId && vendorId.toLowerCase().trim() === supplierValue);

      let dateMatch = true;
      const pDate = new Date(p.invoiceDate || p.date || p.createdAt);
      if (!isNaN(pDate.getTime())) {
         const dS = new Date(pDate.getFullYear(), pDate.getMonth(), pDate.getDate()).getTime();
         
         if (filters.fromDate) {
            const f = new Date(filters.fromDate);
            if (dS < new Date(f.getFullYear(), f.getMonth(), f.getDate()).getTime()) dateMatch = false;
         }
         if (filters.toDate) {
            const t = new Date(filters.toDate);
            if (dS > new Date(t.getFullYear(), t.getMonth(), t.getDate()).getTime()) dateMatch = false;
         }

         if (filters.date && filters.date !== 'all') {
            const now = new Date();
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();

            if (filters.date === 'today' && dS !== today) dateMatch = false;
            if (filters.date === 'this_week') {
               const start = new Date(today);
               start.setDate(start.getDate() - 6);
               if (dS < start.getTime()) dateMatch = false;
            }
            if (filters.date === 'this_month') {
               const start = new Date(today);
               start.setDate(start.getDate() - 30);
               if (dS < start.getTime()) dateMatch = false;
            }
            if (filters.date === 'this_year') {
               const start = new Date(today);
               start.setDate(start.getDate() - 364);
               if (dS < start.getTime()) dateMatch = false;
            }
         }
      }

      return searchMatch && categoryMatch && supplierMatch && dateMatch;
    });
  }, [openStock, search, filters]);

  return {
    user, inventory, activeInventory, activeNotifications, materialRequests, filteredRequests, materialReturns, filteredReturns, projects: projectsLocal,
    sales, filteredSales, consolidatedSales, purchaseReturns, filteredPurchaseReturns, saleReturns, filteredSaleReturns, consolidatedReturns, requestStatusOptions,
    activeTab, setActiveTab, subActiveTab, setSubActiveTab,
    inventorySubTab, setInventorySubTab,
    returnsSubTab, setReturnsSubTab,
    viewMode, setViewMode,
    search, setSearch,
    filters, setFilters,
    sortBy, setSortBy,
    showAdvancedFilters, setShowAdvancedFilters,
    cats: filterCats, lowStock, noStockCount: noStock.length, filtered, totalValue, itemCount: allAggregatedItems.length,
    totalUnits, averageValuePerItem,
    statsBreakdown, requestStats, returnStats, activeReturnStats, activeReturnFilterOptions, activeSaleFilterOptions, purchaseStats, saleStats, openStockStats,
    isExpanded, setIsExpanded,
    addOpen, setAddOpen, restockItem, setRestockItem, restockQty, setRestockQty,
    restockReturn, setRestockReturn,
    form, setForm, selReq, setSelReq, selReturn, setSelReturn,
    saleOpen, setSaleOpen, purchaseOpen, setPurchaseOpen, purchaseReturnOpen, setPurchaseReturnOpen, saleReturnOpen, setSaleReturnOpen, openStockOpen, setOpenStockOpen,
    selSale, setSelSale, selPR, setSelPR, selSR, setSelSR,
    selectedPRForPayment, setSelectedPRForPayment,
    initialReturnInvoice, setInitialReturnInvoice,
    dispatchReq, setDispatchReq,
    restock, finalRestockReturn, addItem, addItemToList, removeItemFromList, editItemInList, handleAddNewItem, handleNewPurchase, handleNewOpenStock, handleEditOpenStock, deleteItem, handleEdit, approveRequest, ownerAction, handleDispatch, handleDeliver, handleConfirmReturn, loading,
    fetchPurchaseReturns, fetchSaleReturns,
    viewItem, setViewItem,
    handleViewPurchases,
    dynamicSettings: { cats, units, suppliers, brands, stores, masterItems },
    editingItem,
    handleSelectExistingItem,
    distributedMap,
    getInventorySubTabs: () => [
      { id: "items", label: "Inventory Master" },
      { id: "purchases", label: "Purchases" },
      { id: "sales", label: "Sales" },
      { id: "return_purchases", label: "Purchase Returns" },
      { id: "return_site", label: "Sale Returns" },
      { id: "return_damaged", label: "Damaged Items" }
      // { id: "open_stock", label: "Open Stock" }
    ],
    submitting: loading,
    rawInventory: inventory,
    masterInventory,
    allAggregatedItems,
    distributedMap: new Map(),
    purchases,
    filteredPurchases,
    openingStocks: openStock,
    filteredOpenStock,
    inventory: activeInventory,
    refreshAll,
    handlePurchaseReturnPayment
  };
};
